import React, { useState, useMemo, useEffect } from 'react';
import PocketBase from 'pocketbase';

const pb = new PocketBase('https://bahcarplaza.com');

function CompareCars({ view, setView, cars = [], setSelectedCar, selectedCar }) {
  
  // Auto-Select Logic: Details page থেকে এলে প্রথম গাড়িটি অটো সিলেক্ট হবে
  const [selectedCars, setSelectedCars] = useState(() => {
    if (selectedCar && selectedCar.id) {
      return [selectedCar, null]; 
    }
    return [null, null];
  });
  
  const [activeSlot, setActiveSlot] = useState(null); 
  const [searchQuery, setSearchQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getVal = (val) => (Array.isArray(val) ? val[0] : val || "-");
  const getFirstImage = (car) => {
    if (!car || !car.image) return null;
    const images = Array.isArray(car.image) ? car.image : [car.image];
    return images.length > 0 ? `https://bahcarplaza.com/api/files/${car.collectionId}/${car.id}/${images[0]}` : null;
  };

  const addSlot = () => {
    if (selectedCars.length < 3) setSelectedCars([...selectedCars, null]);
  };

  const removeCar = (index) => {
    const newArr = [...selectedCars];
    newArr[index] = null;
    setSelectedCars(newArr);
  };

  const searchResults = useMemo(() => {
    if (!searchQuery) return cars.filter(c => c.status === 'Approved').slice(0, 20); 
    const lowerQ = searchQuery.toLowerCase();
    return cars.filter(c => 
      c.status === 'Approved' && 
      ((c.brand && c.brand.toLowerCase().includes(lowerQ)) || 
       (c.model && c.model.toLowerCase().includes(lowerQ)))
    );
  }, [cars, searchQuery]);

  const handleSelectCar = (car) => {
    if (selectedCars.find(c => c && c.id === car.id)) {
      alert("This car is already added to comparison!");
      return;
    }
    const newArr = [...selectedCars];
    newArr[activeSlot] = car;
    setSelectedCars(newArr);
    setActiveSlot(null);
    setSearchQuery("");
  };

  // 🚨 Specs Table Configuration 🚨
  const specs = [
    { label: 'Price', key: 'price', isCurrency: true },
    { label: 'Body Type', key: 'body_type' },
    { label: 'Mfg Year', key: 'year' },
    { label: 'Fuel Type', key: 'fuel_type' },
    { label: 'Transmission', key: 'transmission' },
    { label: 'Mileage', key: 'mileage', suffix: ' KMPL' },
    { label: 'Seats', key: 'seats' },
    { label: 'Owner', key: 'owner_type' },
    { label: 'KM Driven', key: 'kilometers_driven', suffix: ' KM' },
    { label: 'Location', key: 'city' }
  ];

  return (
    <div className="min-h-screen bg-white pb-24 font-sans flex flex-col relative overflow-hidden">
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-slideUp { animation: slideUp 0.3s ease-out forwards; }
        `}
      </style>

      {/* 🚨 HEADER 🚨 */}
      {/* Changed absolute/fixed positioning to relative if needed, but keeping fixed ensures it stays on top.
          Z-index increased to ensure it stays above the sticky table headers. */}
      <div className={`fixed top-0 left-0 right-0 z-[70] bg-[#012b72] py-3 md:py-4 px-4 flex items-center gap-3 transition-all duration-300 ${isScrolled ? 'shadow-md' : 'shadow-none'}`}>
        <button onClick={() => setView(selectedCar ? 'details' : 'home')} className="bg-white/20 w-8 h-8 rounded-full border-none text-white flex items-center justify-center cursor-pointer font-bold active:scale-95 transition-transform">←</button>
        <h2 className="text-white font-black text-[16px] uppercase m-0 tracking-wide">Compare Cars</h2>
      </div>

      {/* 🚨 Adjusted Padding to remove extra gap 🚨 */}
      <div className="pt-[52px] md:pt-[60px]">
        
        {/* 🚨 TABLE CONTAINER 🚨 */}
        <div className="w-full overflow-x-auto hide-scrollbar bg-white relative">
          <div className="min-w-max w-full">
            
            {/* 🚨 STICKY CAR HEADER ROW 🚨 */}
            {/* Z-index must be lower than main header (70) but higher than spec labels (30).
                Top value matches the height of the main header to sit perfectly below it. */}
            <div className="sticky top-[52px] md:top-[60px] z-40 bg-white border-b-4 border-gray-100 flex shadow-sm">
              
              {/* Empty top-left sticky block - prevents overlap with left spec labels */}
              <div className="w-[100px] md:w-[130px] flex-shrink-0 bg-white border-r border-gray-100 sticky left-0 z-50"></div>

              {selectedCars.map((car, index) => (
                <div key={index} className="w-[160px] md:w-[200px] flex-shrink-0 p-3 border-r border-gray-100 relative flex flex-col justify-between bg-white">
                  {car ? (
                    <>
                      <button onClick={() => removeCar(index)} className="absolute top-2 right-2 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center border-none text-[10px] font-bold cursor-pointer z-10 shadow-sm active:scale-90">✕</button>
                      <div className="h-[90px] w-full bg-gray-100 rounded-[10px] overflow-hidden shrink-0 mb-3 border border-gray-100">
                        <img src={getFirstImage(car)} alt="car" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex flex-col text-center flex-1 justify-center">
                        <h4 className="text-[12px] font-black text-[#002f34] uppercase leading-tight m-0 line-clamp-2 min-h-[30px] flex items-center justify-center">{car.brand} {car.model}</h4>
                        <p className="text-[#ef5e33] font-black text-[14px] m-0 mt-1 mb-2">₹{car.price?.toLocaleString()}</p>
                        <button onClick={() => { setSelectedCar(car); setView('details'); }} className="mt-auto w-full py-2 bg-[#002f34] text-white text-[10px] font-black uppercase rounded-[8px] border-none shadow-sm active:scale-95 cursor-pointer">Details</button>
                      </div>
                    </>
                  ) : (
                    <div onClick={() => setActiveSlot(index)} className="h-full min-h-[170px] bg-[#f9fcfb] flex flex-col items-center justify-center cursor-pointer p-2 active:scale-95 transition-transform border-2 border-dashed border-[#b0c4c1] rounded-[12px]">
                      <div className="w-10 h-10 rounded-full bg-blue-50 text-[#012b72] flex items-center justify-center text-xl font-black mb-2 shadow-sm">+</div>
                      <span className="text-[#002f34] font-black text-[11px] uppercase text-center">Add Car</span>
                    </div>
                  )}
                </div>
              ))}

              {/* Add 3rd Car Button */}
              {selectedCars.length < 3 && (
                <div className="w-[160px] md:w-[200px] flex-shrink-0 p-3 bg-white border-r border-gray-100">
                  <div onClick={addSlot} className="h-full min-h-[170px] bg-[#fff9f6] rounded-[12px] border-2 border-dashed border-[#ef5e33] flex flex-col items-center justify-center cursor-pointer p-2 active:scale-95 transition-transform">
                    <div className="w-10 h-10 rounded-full bg-[#ef5e33] text-white flex items-center justify-center text-xl font-black mb-2 shadow-sm">+</div>
                    <span className="text-[#ef5e33] font-black text-[11px] uppercase text-center leading-tight mt-1">Add 3rd Car</span>
                  </div>
                </div>
              )}

            </div>

            {/* 🚨 TABLE SPECS ROWS 🚨 */}
            <div className="flex flex-col w-full">
              {specs.map((spec, i) => (
                <div key={i} className="flex w-full border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  
                  {/* Sticky Spec Label on the Left */}
                  {/* Z-index 30 ensures it stays below the top header rows when scrolling down */}
                  <div className="w-[100px] md:w-[130px] flex-shrink-0 bg-[#f4f7f6] p-3 flex items-center justify-center text-center border-r border-gray-200 sticky left-0 z-30 shadow-[2px_0_5px_rgba(0,0,0,0.03)]">
                    <span className="text-[#002f34] font-black text-[10px] uppercase tracking-widest leading-tight">{spec.label}</span>
                  </div>

                  {/* Spec Values - 🚨 PERFECTLY CENTERED ALIGNMENT 🚨 */}
                  {selectedCars.map((car, idx) => (
                    <div key={idx} className="w-[160px] md:w-[200px] flex-shrink-0 p-4 flex items-center justify-center text-center border-r border-gray-100 bg-white">
                      {car ? (
                        <span className={`font-black text-[12px] md:text-[13px] leading-tight uppercase text-center w-full block ${spec.isCurrency ? 'text-[#ef5e33]' : 'text-[#222]'}`}>
                          {spec.isCurrency ? '₹' : ''}
                          {spec.isCurrency ? car[spec.key]?.toLocaleString() : getVal(car[spec.key])}
                          {spec.suffix ? spec.suffix : ''}
                        </span>
                      ) : (
                        <span className="text-gray-300 font-bold text-center w-full block">-</span>
                      )}
                    </div>
                  ))}
                  
                  {/* Empty space for 3rd car slot if not filled */}
                  {selectedCars.length < 3 && <div className="w-[160px] md:w-[200px] flex-shrink-0 p-3 bg-white"></div>}
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      {/* 🚨 Search Modal to Add Car 🚨 */}
      {activeSlot !== null && (
        <div className="fixed inset-0 bg-[#002f34]/80 z-[9999] flex flex-col justify-end backdrop-blur-sm">
          <div className="absolute inset-0" onClick={() => setActiveSlot(null)}></div>
          <div className="bg-white w-full rounded-t-[30px] p-5 shadow-2xl relative flex flex-col h-[85vh] animate-slideUp">
            
            <div className="flex justify-between items-center mb-4">
               <h3 className="font-black text-[18px] text-[#002f34] m-0 uppercase">Select Car to Compare</h3>
               <button onClick={() => setActiveSlot(null)} className="bg-gray-100 border-none w-8 h-8 rounded-full font-bold cursor-pointer text-gray-500 hover:bg-gray-200 transition-colors">✕</button>
            </div>

            <div className="bg-[#f4f7f6] rounded-xl flex items-center px-4 py-3 mb-4 border border-gray-200 focus-within:border-[#ef5e33] transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-3"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input type="text" placeholder="Search by brand or model..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="flex-1 bg-transparent border-none outline-none font-bold text-[#002f34] text-[13px]" autoFocus />
            </div>

            <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col gap-3 pb-6">
              {searchResults.length > 0 ? searchResults.map((car) => {
                const isAlreadyAdded = selectedCars.find(c => c && c.id === car.id);
                return (
                  <div key={car.id} onClick={() => !isAlreadyAdded && handleSelectCar(car)} className={`flex gap-3 bg-white border p-2 rounded-2xl shadow-sm transition-transform ${isAlreadyAdded ? 'border-red-300 opacity-50 cursor-not-allowed' : 'border-gray-100 cursor-pointer active:scale-95 hover:border-[#ef5e33]'}`}>
                    <img src={getFirstImage(car)} alt="car" className="w-[80px] h-[60px] rounded-xl object-cover bg-gray-100 shrink-0" />
                    <div className="flex flex-col justify-center flex-1">
                      <h4 className="text-[#002f34] font-black text-[13px] uppercase m-0 leading-tight">{car.brand} {car.model}</h4>
                      <div className="flex gap-2 items-center mt-1">
                        <span className="text-[#ef5e33] font-black text-[12px]">₹{car.price?.toLocaleString()}</span>
                        <span className="text-gray-400 font-bold text-[9px] px-1.5 py-0.5 bg-gray-50 rounded uppercase">{car.year}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center px-2">
                      {isAlreadyAdded ? (
                        <span className="text-[10px] font-black text-red-500 uppercase text-center w-12 leading-tight">Already Added</span>
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-[#f4f7f6] flex items-center justify-center text-[#002f34] font-black text-lg">+</span>
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="flex flex-col items-center justify-center mt-10">
                  <span className="text-4xl grayscale opacity-30 mb-2">🔍</span>
                  <p className="text-center text-gray-400 font-bold">No cars found matching "{searchQuery}"</p>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default CompareCars;